import { TypographyCaption } from '@heidi/design-system/typography';
import { Fragment, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { LabelledContextPanel } from '../../inline-approval/labelled-context-panel';
import type { MergeSessionsApprovalFields } from './ApprovalRequestBlock.display';

/**
 * Clinician-friendly body for the ``merge_sessions`` HITL approval card.
 * Replaces the raw ``tool_input`` JSON dump with two readable parts:
 *
 *   1. A heads-up banner stating plainly what merging does — the sessions are
 *      grouped into one, the members are hidden behind the group, and it is
 *      reversible (unlink) so nothing is deleted.
 *   2. A "Sessions" panel listing the anchor session plus every session being
 *      folded in. The agent's tool input carries only ids, so the page resolves
 *      each session's name + date and renders it through ``renderSession`` with
 *      ``MergeSessionRow``, so the clinician can verify WHICH sessions they are
 *      merging; the id (truncated, monospace) stays as an audit anchor and as
 *      the fallback while the lookup loads or a session isn't accessible.
 */
export interface MergeSessionsApprovalDetailsProps {
  fields: MergeSessionsApprovalFields;
  renderSession: (sessionId: string) => ReactNode;
}

export interface MergeSessionRowProps {
  sessionId: string;
  name: string | null | undefined;
  createdTimeLocal: string | null | undefined;
}

const shortId = (id: string): string => `…${id.slice(-8)}`;

/**
 * One session row: the session name + created time, so the clinician sees
 * human-readable context, not just an opaque id. Falls back to the truncated
 * id while loading or when the session isn't accessible.
 */
export const MergeSessionRow = ({
  sessionId,
  name: rawName,
  createdTimeLocal,
}: MergeSessionRowProps) => {
  const intl = useIntl();
  const name = rawName?.trim();

  return (
    <li className="flex flex-col gap-0.5">
      <span className="break-words text-sm leading-snug text-text-primary [overflow-wrap:anywhere]">
        {name ||
          intl.formatMessage({ defaultMessage: 'Untitled session', id: 'VZOiVMXn0z' })}
        <span className="ms-1.5 font-mono text-xs text-text-secondary">
          {shortId(sessionId)}
        </span>
      </span>
      {createdTimeLocal && (
        <span className="text-xs leading-snug text-text-secondary">
          {intl.formatDate(new Date(createdTimeLocal), {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </span>
      )}
    </li>
  );
};

export const MergeSessionsApprovalDetails = ({
  fields,
  renderSession,
}: MergeSessionsApprovalDetailsProps) => {
  const intl = useIntl();
  const { anchorSessionId, mergeSessionIds } = fields;

  // The anchor is merged together with the listed sessions; de-dupe it out of
  // the "and" list so it isn't shown twice.
  const otherIds = mergeSessionIds.filter((id) => id !== anchorSessionId);
  const totalCount = (anchorSessionId ? 1 : 0) + otherIds.length;

  return (
    <div className="flex flex-col gap-3" data-testid="merge-sessions-details">
      <p className="rounded-md border border-validation-warning/30 bg-validation-warning/10 px-3 py-2 text-xs leading-snug text-text-primary">
        {intl.formatMessage({
          defaultMessage:
            'These sessions are grouped into one. The individual sessions are hidden behind the group (not deleted) and their notes and documents move onto it — you can unlink them later to undo this.',
          id: 'kWa7dTd3o7',
        })}
      </p>

      <LabelledContextPanel
        label={intl.formatMessage(
          {
            defaultMessage: 'Merging {count, plural, one {# session} other {# sessions}}',
            id: 'nCz++8YJi6',
          },
          { count: totalCount }
        )}
      >
        <dl className="flex flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <dt>
              <TypographyCaption className="font-medium text-text-secondary">
                {intl.formatMessage({
                  defaultMessage: 'Anchor session',
                  id: 'AhdwrsvLWQ',
                })}
              </TypographyCaption>
            </dt>
            <dd className="text-sm leading-snug text-text-primary">
              {anchorSessionId ? (
                <ul>{renderSession(anchorSessionId)}</ul>
              ) : (
                <span className="text-text-secondary">
                  {intl.formatMessage({
                    defaultMessage: 'Not specified',
                    id: 'Rk7UyMqysf',
                  })}
                </span>
              )}
            </dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt>
              <TypographyCaption className="font-medium text-text-secondary">
                {intl.formatMessage({
                  defaultMessage: 'Merging in',
                  id: '3plNg4Id3N',
                })}
              </TypographyCaption>
            </dt>
            <dd className="text-sm leading-snug text-text-primary">
              {otherIds.length > 0 ? (
                <ul className="flex flex-col gap-1">
                  {otherIds.map((id) => (
                    <Fragment key={id}>{renderSession(id)}</Fragment>
                  ))}
                </ul>
              ) : (
                <span className="text-text-secondary">
                  {intl.formatMessage({
                    defaultMessage: 'No additional sessions specified.',
                    id: 'hSbyyWBDcL',
                  })}
                </span>
              )}
            </dd>
          </div>
        </dl>
      </LabelledContextPanel>
    </div>
  );
};
